import type { UserType } from '../services/api/models/UserType';

/**
 * Display labels for agency-scoped clinical titles (EMR-699), in the order
 * pickers list them: most common in home health first, Other last.
 */
const USER_TYPE_LABELS: Record<UserType, string> = {
  RN: 'RN',
  PT: 'PT',
  LPN_LVN: 'LPN/LVN',
  OT: 'OT',
  PTA: 'PTA',
  HHA: 'HHA',
  CNA: 'CNA',
  COTA: 'COTA',
  ST: 'ST',
  MSW: 'MSW',
  CHAPLAIN: 'Chaplain',
  OTHER: 'Other',
};

export const USER_TYPE_OPTIONS: Array<{ value: UserType; label: string }> = (
  Object.keys(USER_TYPE_LABELS) as UserType[]
).map(value => ({
  value,
  label: USER_TYPE_LABELS[value],
}));

export function userTypeLabel(userType: UserType | null | undefined): string {
  if (!userType) return '';
  return USER_TYPE_LABELS[userType];
}

/** Collapse a credential token to letters/digits only, upper-cased, for dedup comparisons. */
function normalizeCredentialToken(token: string): string {
  return token.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/**
 * Split a credentials/label string on ',' and '/' into normalized comparison
 * tokens. Splitting on '/' too lets a combined title like "LPN/LVN" match
 * credentials listed either as one token ("LPN/LVN") or two ("LPN, LVN").
 */
function credentialTokens(value: string): Set<string> {
  const tokens = new Set<string>();
  for (const commaPart of value.split(',')) {
    for (const slashPart of commaPart.split('/')) {
      const normalized = normalizeCredentialToken(slashPart);
      if (normalized) tokens.add(normalized);
    }
  }
  return tokens;
}

/** Labels for one title or several, in the order given, dropping the blanks. */
function userTypeLabels(
  userType: UserType | UserType[] | null | undefined
): string[] {
  if (userType === null || userType === undefined) return [];
  const values = Array.isArray(userType) ? userType : [userType];
  return values
    .map(value => userTypeLabel(value))
    .filter((label): label is string => Boolean(label));
}

/**
 * Mirrors the backend's `format_user_display_name`. `userType` takes one title
 * or a list of them, so someone holding more than one reads
 * "Jane Doe, LVN, CNA"; order is the caller's, which for a `user_agencies` row
 * means the primary title first.
 */
export function appendUserTypeToDisplayName(
  name: string,
  userType: UserType | UserType[] | null | undefined,
  credentials?: string | null
): string {
  const trimmed = name.trim();
  const suffixes: string[] = [];
  const cred = credentials?.trim();
  // Grows as titles land, so a title already spelled out in the credentials
  // string or by an earlier title is not printed twice.
  const covered = credentialTokens(cred ?? '');
  if (cred) suffixes.push(cred);
  for (const typeLabel of userTypeLabels(userType)) {
    const labelTokens = credentialTokens(typeLabel);
    const isFullyCovered = [...labelTokens].every(token => covered.has(token));
    if (isFullyCovered) continue;
    suffixes.push(typeLabel);
    for (const token of labelTokens) covered.add(token);
  }
  if (!trimmed) return suffixes.join(', ');
  if (suffixes.length === 0) return trimmed;
  return `${trimmed}, ${suffixes.join(', ')}`;
}
